import React from "react";
import { jsPDF } from "jspdf";

// Helper: desenha o Esquadro e Compasso padrão quando a Loja não tem logo cadastrado
const _drawCompassSquare = (doc, cx, topY) => {
  doc.setDrawColor(184, 134, 11);
  doc.setLineWidth(0.7);
  const size = 8;
  // Compasso (V invertido)
  doc.line(cx - size, topY + size, cx, topY);
  doc.line(cx + size, topY + size, cx, topY);
  // Esquadro
  doc.line(cx - size, topY + size / 2, cx + size, topY + size / 2);
};

/**
 * Gera um Comunicado Maçônico (estilo Diploma) em PDF para leitura em Sessão.
 * Anuncia aniversários maçônicos, elevações a Emérito, Provecto, ou marcos importantes.
 *
 * Uso:
 *   generateHonorCommunique({
 *     memberName: "José da Silva",
 *     cim: "12345",
 *     lodgeName: "A.R.L.S. Luz e Harmonia nº 789",
 *     event: "20 anos de Iniciação",
 *     eventDate: "2026-06-15",
 *     description: "Faltam 5 anos para ser elevado a Provecto",
 *   });
 */
export const generateHonorCommunique = ({
  memberName,
  cim,
  lodgeName = "Loja Maçônica",
  event,
  eventDate,
  description = "",
  city = "",
  logoB64 = null,
}) => {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape" });
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();

  // Bordas ornamentais (moldura dupla dourada)
  doc.setDrawColor(184, 134, 11);
  doc.setLineWidth(1.5);
  doc.rect(10, 10, w - 20, h - 20);
  doc.setLineWidth(0.4);
  doc.rect(14, 14, w - 28, h - 28);

  const cx = w / 2;
  const topY = 20;

  // Cabeçalho: logo da Loja (se existir) OU símbolo do Esquadro e Compasso
  if (logoB64) {
    try {
      const cleanB64 = logoB64.startsWith("data:") ? logoB64 : `data:image/png;base64,${logoB64}`;
      // Detecta tipo pelo prefixo — jsPDF suporta PNG e JPEG
      const isJpeg = cleanB64.includes("image/jpeg") || cleanB64.includes("image/jpg");
      const format = isJpeg ? "JPEG" : "PNG";
      const logoSize = 22;
      doc.addImage(cleanB64, format, cx - logoSize / 2, topY, logoSize, logoSize, undefined, "FAST");
    } catch (err) {
      // Se der erro (base64 corrompido, ex.), cai no símbolo padrão
      _drawCompassSquare(doc, cx, topY + 3);
    }
  } else {
    _drawCompassSquare(doc, cx, topY + 3);
  }

  // Título "A.·. G.·. D.·. G.·. A.·. D.·. U.·."
  doc.setFont("times", "italic");
  doc.setFontSize(11);
  doc.setTextColor(120, 90, 20);
  doc.text("À Glória do Grande Arquiteto do Universo", cx, 50, { align: "center" });

  // Nome da Loja
  doc.setFont("times", "bold");
  doc.setFontSize(15);
  doc.setTextColor(0, 0, 0);
  doc.text(lodgeName, cx, 64, { align: "center" });
  doc.setDrawColor(184, 134, 11);
  doc.setLineWidth(0.3);
  doc.line(cx - 80, 68, cx + 80, 68);

  // Cabeçalho "COMUNICADO"
  doc.setFont("times", "bolditalic");
  doc.setFontSize(28);
  doc.setTextColor(184, 134, 11);
  doc.text("COMUNICADO", cx, 82, { align: "center" });

  // Sub: EVENT
  doc.setFont("times", "bold");
  doc.setFontSize(16);
  doc.setTextColor(60, 60, 60);
  doc.text(event, cx, 96, { align: "center" });

  // Corpo — dedicatória
  doc.setFont("times", "normal");
  doc.setFontSize(13);
  doc.setTextColor(0, 0, 0);
  const bodyY = 115;
  doc.text("O Ir.·.", cx, bodyY, { align: "center" });

  // Nome do irmão em destaque
  doc.setFont("times", "bold");
  doc.setFontSize(20);
  doc.setTextColor(120, 60, 40);
  doc.text(memberName, cx, bodyY + 12, { align: "center" });
  if (cim) {
    doc.setFont("times", "italic");
    doc.setFontSize(11);
    doc.setTextColor(120, 90, 20);
    doc.text(`CIM ${cim}`, cx, bodyY + 20, { align: "center" });
  }

  // Descrição / evento
  doc.setFont("times", "normal");
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  const paragraphY = bodyY + 34;
  const eventDateStr = eventDate ? new Date(eventDate + "T00:00").toLocaleDateString("pt-BR") : "";
  const paragraph = description ||
    `em ${eventDateStr}, a Loja registra e celebra este marco fraternal, prestando as devidas homenagens ao Ir.·. acima nomeado.`;
  const maxWidth = w - 80;
  const wrapped = doc.splitTextToSize(paragraph, maxWidth);
  wrapped.forEach((line, idx) => {
    doc.text(line, cx, paragraphY + idx * 6, { align: "center" });
  });

  // Data de emissão
  const today = new Date().toLocaleDateString("pt-BR");
  doc.setFont("times", "italic");
  doc.setFontSize(11);
  doc.setTextColor(90, 90, 90);
  doc.text(`${city ? city + ", " : ""}${today}`, cx, h - 45, { align: "center" });

  // Linhas de assinatura
  doc.setDrawColor(120, 120, 120);
  doc.setLineWidth(0.3);
  doc.line(cx - 70, h - 30, cx - 20, h - 30);
  doc.line(cx + 20, h - 30, cx + 70, h - 30);
  doc.setFont("times", "italic");
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  doc.text("Ir.·. Secretário", cx - 45, h - 25, { align: "center" });
  doc.text("Ir.·. Venerável Mestre", cx + 45, h - 25, { align: "center" });

  // Rodapé decorativo
  doc.setFont("times", "italic");
  doc.setFontSize(9);
  doc.setTextColor(184, 134, 11);
  doc.text("~ S.·.F.·.U.·. ~", cx, h - 15, { align: "center" });

  doc.save(`Comunicado_${memberName.replace(/[^a-zA-Z0-9]/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
};

/**
 * Detecta os marcos maçônicos "próximos" ou já atingidos para um membro,
 * baseado em suas datas ritualísticas + estado atual.
 * Retorna lista ordenada de eventos anunciáveis.
 */
export const detectMasonicMilestones = (member) => {
  if (!member?.initiation_date) return [];
  const today = new Date();
  const initDate = new Date(member.initiation_date + "T00:00");
  const yearsSinceInit = (today - initDate) / (365.25 * 24 * 60 * 60 * 1000);

  const milestones = [];

  // Aniversários redondos de iniciação
  [5, 10, 15, 20, 25, 30, 40, 50].forEach((years) => {
    const targetDate = new Date(initDate);
    targetDate.setFullYear(targetDate.getFullYear() + years);
    const diffDays = Math.abs((today - targetDate) / (24 * 60 * 60 * 1000));
    if (diffDays <= 60) {
      milestones.push({
        type: "aniversario_iniciacao",
        years,
        event: `${years} anos de Iniciação`,
        date: targetDate.toISOString().slice(0, 10),
        description: `O Ir.·. ${member.name} completa ${years} anos desde a sua Iniciação nos Mistérios da Ordem, marco de dedicação e perseverança na Arte Real.`,
      });
    }
  });

  // Elevação a Emérito registrada
  if (member.emerito_date) {
    const emDate = new Date(member.emerito_date + "T00:00");
    const diffDays = Math.abs((today - emDate) / (24 * 60 * 60 * 1000));
    if (diffDays <= 60) {
      milestones.push({
        type: "emerito",
        event: "Elevação a Maçom Emérito",
        date: member.emerito_date,
        description: `Comunica-se ao Quadro que o Ir.·. ${member.name} foi elevado ao Título de Maçom Emérito, em reconhecimento à sua dedicação vitalícia à Loja.`,
      });
    }
  }

  // Elevação a Provecto registrada
  if (member.provecto_date) {
    const prDate = new Date(member.provecto_date + "T00:00");
    const diffDays = Math.abs((today - prDate) / (24 * 60 * 60 * 1000));
    if (diffDays <= 60) {
      milestones.push({
        type: "provecto",
        event: "Elevação a Maçom Provecto",
        date: member.provecto_date,
        description: `Comunica-se ao Quadro que o Ir.·. ${member.name} foi elevado ao Título de Maçom Provecto, distinção máxima concedida aos Irmãos de longa jornada e reta conduta.`,
      });
    }
  }

  // Aviso "5 anos para Provecto"
  if (!member.provecto_date && yearsSinceInit >= 20) {
    const provectoTarget = new Date(initDate);
    provectoTarget.setFullYear(provectoTarget.getFullYear() + 25);
    const diffYears = (provectoTarget - today) / (365.25 * 24 * 60 * 60 * 1000);
    if (diffYears > 0 && diffYears <= 5) {
      const years = Math.floor(diffYears);
      const months = Math.floor((diffYears - years) * 12);
      milestones.push({
        type: "aviso_provecto",
        event: "Contagem regressiva ao Provecto",
        date: provectoTarget.toISOString().slice(0, 10),
        description: `O Ir.·. ${member.name} está a ${years} ano${years !== 1 ? "s" : ""}${months > 0 ? ` e ${months} ${months === 1 ? "mês" : "meses"}` : ""} da possível elevação a Provecto. Que este período seja de assiduidade e edificação.`,
      });
    }
  }

  return milestones;
};
